import { IconPhoto } from '@tabler/icons-react-native';
import { PCImage } from 'app/design/primitives/image';
import type { SuggestedListing } from 'app/network/suggested-listing';
import { useTheme } from 'app/shopify';
import { useCallback, useEffect, useState } from 'react';
import { StyleSheet, useWindowDimensions } from 'react-native';
import { Gesture, GestureDetector } from 'react-native-gesture-handler';
import Animated, {
  cancelAnimation,
  type SharedValue,
  useAnimatedStyle,
  useAnimatedReaction,
  useDerivedValue,
  useReducedMotion,
  useSharedValue,
  withDelay,
  withSequence,
  withSpring,
  withTiming,
} from 'react-native-reanimated';
import { scheduleOnRN } from 'react-native-worklets';
import { autoplayCarouselFrom } from './carousel-animation';
import { CAROUSEL_CARD_WIDTH, CAROUSEL_PERSPECTIVE, carouselPose, wrapCarouselIndex } from './carousel-geometry';

function createCarouselPan(
  active: boolean,
  count: number,
  dragging: SharedValue<boolean>,
  gestureStart: SharedValue<number>,
  progress: SharedValue<number>,
  snapTo: (target: number) => void,
) {
  return Gesture.Pan()
    .enabled(active && count > 1)
    .activeOffsetX([-12, 12])
    .failOffsetY([-14, 14])
    .onStart(() => {
      cancelAnimation(progress);
      gestureStart.set(progress.value);
      dragging.set(true);
    })
    .onUpdate((event) => {
      const proposed = gestureStart.value - event.translationX / DRAG_DISTANCE;
      progress.set(Math.max(gestureStart.value - 1, Math.min(gestureStart.value + 1, proposed)));
    })
    .onEnd((event) => {
      const projected = progress.value - event.velocityX / 1800;
      // Clamp to integer destinations even if autoplay was interrupted
      // between cards; otherwise snapping could stop at a fractional pose.
      const first = Math.ceil(gestureStart.value - 1);
      const last = Math.floor(gestureStart.value + 1);
      snapTo(Math.max(first, Math.min(last, Math.round(projected))));
    })
    .onFinalize((_event, success) => {
      if (dragging.value && !success) snapTo(Math.round(progress.value));
      dragging.set(false);
    });
}

const CARD_HEIGHT = 150;
const DRAG_DISTANCE = 150;

interface RevolvingCardProps {
  item: SuggestedListing;
  index: number;
  count: number;
  progress: SharedValue<number>;
  reveal: SharedValue<number>;
  spread: number;
}

function RevolvingCard({ item, index, count, progress, reveal, spread }: RevolvingCardProps) {
  const theme = useTheme();
  const pose = useDerivedValue(() => carouselPose(index, progress.value, count, spread));
  // Untransformed siblings own stacking; inner views own perspective/shadows.
  // All layers are calculated on the UI thread, without React reordering.
  const layerStyle = useAnimatedStyle(() => ({ zIndex: pose.value.layer }));
  const cardStyle = useAnimatedStyle(() => {
    const { depth, translateX, translateY, rotateY, scale } = pose.value;
    return {
      opacity: 1,
      shadowOpacity: 0.1 + depth * 0.18,
      shadowRadius: 6 + depth * 6,
      transform: [
        { perspective: CAROUSEL_PERSPECTIVE },
        { translateX },
        { translateY: translateY + (1 - reveal.value) * 36 },
        { rotateY: `${String(rotateY)}deg` },
        { scale },
      ],
    };
  });
  const lightingStyle = useAnimatedStyle(() => ({ opacity: (1 - pose.value.depth) * 0.16 }));

  return (
    <Animated.View
      testID={`offer-hub-preview-${item.id}`}
      accessible={false}
      importantForAccessibility="no"
      collapsable={false}
      style={[styles.cardLayer, layerStyle]}
    >
      <Animated.View style={[styles.cardShadow, cardStyle]}>
        <Animated.View style={styles.cardClip}>
          {item.listingGroup.picture ? (
            <PCImage source={{ uri: item.listingGroup.picture }} contentFit="cover" style={StyleSheet.absoluteFill} />
          ) : (
            <Animated.View style={styles.fallback}>
              <IconPhoto size={32} color={theme.colors['icon.tertiary']} />
            </Animated.View>
          )}
          <Animated.View pointerEvents="none" style={[StyleSheet.absoluteFill, styles.lighting, lightingStyle]} />
        </Animated.View>
      </Animated.View>
    </Animated.View>
  );
}

export interface RevolvingCardCarouselProps {
  items: SuggestedListing[];
  accessibilityLabel: string;
  active: boolean;
}

export function RevolvingCardCarousel({ items, accessibilityLabel, active }: RevolvingCardCarouselProps) {
  const cards = items.slice(0, 3);
  const count = cards.length;
  const identity = cards.map((item) => item.id).join(':');
  const reducedMotion = useReducedMotion();
  const { width } = useWindowDimensions();
  // Even at the narrowest width, equal-depth cards are horizontally separated
  // when their layer order changes (sqrt(3) * 66 > CARD_WIDTH).
  const spread = Math.min(72, Math.max(66, (width - 180) / 2));
  const progress = useSharedValue(0);
  const gestureStart = useSharedValue(0);
  const dragging = useSharedValue(false);
  const reveal = useSharedValue(reducedMotion ? 1 : 0);
  const [centeredIndex, setCenteredIndex] = useState(0);

  // This JS update is for the screen-reader label only, never visual stacking.
  useAnimatedReaction(
    () => wrapCarouselIndex(Math.round(progress.value), count),
    (current, previous) => {
      if (current !== previous) scheduleOnRN(setCenteredIndex, current);
    },
    [count],
  );
  // Native gesture worklets retain these callbacks throughout an active pan.
  const snapTo = useCallback(
    (target: number) => {
      'worklet';
      if (reducedMotion || count < 2) {
        progress.set(target);
      } else {
        progress.set(
          withSequence(
            withSpring(target, { damping: 20, stiffness: 190, mass: 0.72, overshootClamping: true }),
            autoplayCarouselFrom(target, count, { cardDuration: 800, roundPause: 4000 }),
          ),
        );
      }
    },
    [count, progress, reducedMotion],
  );
  const rotateBy = (direction: number) => {
    if (!active || count < 2) return;
    cancelAnimation(progress);
    snapTo(Math.round(progress.value) + direction);
  };

  useEffect(() => {
    cancelAnimation(progress);
    progress.set(0);
    dragging.set(false);
    reveal.set(reducedMotion ? 1 : 0);
    if (active && !reducedMotion) {
      reveal.set(withTiming(1, { duration: 420 }));
      if (count > 1)
        progress.set(withDelay(600, autoplayCarouselFrom(0, count, { cardDuration: 800, roundPause: 4000 })));
    }
    return () => {
      cancelAnimation(progress);
      cancelAnimation(reveal);
    };
  }, [active, count, dragging, identity, progress, reducedMotion, reveal]);

  const pan = createCarouselPan(active, count, dragging, gestureStart, progress, snapTo);

  const activeItem = cards[centeredIndex];
  return (
    <GestureDetector gesture={pan}>
      <Animated.View
        accessible
        accessibilityRole="adjustable"
        accessibilityLabel={accessibilityLabel}
        accessibilityValue={{ text: activeItem?.listingGroup.name ?? undefined }}
        accessibilityActions={[{ name: 'increment' }, { name: 'decrement' }]}
        onAccessibilityAction={(event) => rotateBy(event.nativeEvent.actionName === 'decrement' ? -1 : 1)}
        style={styles.stage}
      >
        {cards.map((item, index) => (
          <RevolvingCard
            key={item.id}
            item={item}
            index={index}
            count={count}
            progress={progress}
            reveal={reveal}
            spread={spread}
          />
        ))}
      </Animated.View>
    </GestureDetector>
  );
}

const styles = StyleSheet.create({
  stage: { position: 'absolute', right: 0, bottom: -28, left: 0, height: CARD_HEIGHT },
  cardLayer: {
    position: 'absolute',
    top: 0,
    left: '50%',
    width: CAROUSEL_CARD_WIDTH,
    height: CARD_HEIGHT,
    marginLeft: -CAROUSEL_CARD_WIDTH / 2,
  },
  cardShadow: {
    flex: 1,
    borderRadius: 18,
    backgroundColor: '#FFFFFF',
    shadowColor: '#10131A',
    shadowOffset: { width: 0, height: 5 },
    // Constant local elevation supplies Android shadows without competing
    // with the outer siblings' depth ordering.
    elevation: 4,
  },
  cardClip: { flex: 1, overflow: 'hidden', borderRadius: 18, backgroundColor: '#FFFFFF' },
  fallback: { flex: 1, alignItems: 'center', justifyContent: 'center' },
  lighting: { backgroundColor: '#111827' },
});
